// 📄 pages/Proyectos/integrationChannels.ts
// Fuente única de "qué Canales de Integración tiene cada solución" (SLP,
// Sourcing, Contracts, ...) — análogo a solucionesConfig.ts pero para los
// canales de integración específicos de cada módulo (Commerce Automation
// ya tiene los suyos hardcodeados en CommerceHome.tsx/HomeCA.tsx y no se
// tocan acá).
//
// Los pasos de cada canal usan campos GENÉRICOS (endpoint, mapeo de
// campos, notas de prueba) en vez de replicar transacciones SAP puntuales
// como hace Commerce (NACE, WE20, BD64, ...): no hay todavía un checklist
// técnico verificado para estos módulos, así que se arranca con una
// estructura simple y editable en vez de inventar pasos SAP específicos
// que podrían ser incorrectos. Ajustar/ampliar acá cuando se defina el
// checklist técnico real de cada canal.
export interface CanalStepFieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea';
  placeholder?: string;
}

export interface CanalStepDef {
  id: string;
  title: string;
  desc: string;
  fields: CanalStepFieldDef[];
}

export type SolucionConCanales = 'slp' | 'sourcing' | 'contracts';

export interface CanalIntegracionDef {
  // Segmento de ruta bajo /proyecto/:projectId/integracion/{solucion}/{slug}
  slug: string;
  solucion: SolucionConCanales;
  label: string;
  icon: string;
  description: string;
  steps: CanalStepDef[];
}

const PASOS_GENERICOS = (prefijoDesc: string): CanalStepDef[] => [
  {
    id: 'parametros',
    title: '1. Parámetros de Conexión',
    desc: `Endpoint y sistema de referencia para ${prefijoDesc}`,
    fields: [
      { key: 'endpoint', label: 'Endpoint / URL del servicio', type: 'text', placeholder: 'https://...' },
      { key: 'sistemaReferencia', label: 'Sistema de referencia (ERP / Ariba)', type: 'text', placeholder: 'Ej: ERPCLNT100' }
    ]
  },
  {
    id: 'mapeo',
    title: '2. Mapeo de Campos',
    desc: 'Correspondencia de campos entre Ariba y el sistema destino',
    fields: [
      { key: 'mapeoCampos', label: 'Mapeo de campos (notas)', type: 'textarea', placeholder: 'Describe el mapeo de campos clave...' }
    ]
  },
  {
    id: 'prueba',
    title: '3. Prueba de Integración',
    desc: 'Validación end-to-end con un registro de prueba',
    fields: [
      { key: 'referenciaPrueba', label: 'Referencia de prueba (ID/Número)', type: 'text' },
      { key: 'resultadoPrueba', label: 'Resultado de la prueba', type: 'textarea' }
    ]
  }
];

export const CANALES_INTEGRACION: CanalIntegracionDef[] = [
  // ── SLP (Supplier Lifecycle and Performance) ──────────────────────────
  {
    slug: 'bp-desde-ariba',
    solucion: 'slp',
    label: 'Integración BP desde Ariba',
    icon: '🔗',
    description: 'Configuración de la integración de Business Partner / Proveedores hacia el ERP.',
    steps: PASOS_GENERICOS('la integración de Business Partner hacia el ERP')
  },
  {
    slug: 'bp-exportacion-erp',
    solucion: 'slp',
    label: 'Exportación de BP desde ERP',
    icon: '📤',
    description: 'Exportación/sincronización de proveedores del ERP hacia Ariba.',
    steps: PASOS_GENERICOS('la exportación de proveedores desde el ERP hacia Ariba')
  },

  // ── Sourcing ───────────────────────────────────────────────────────────
  {
    slug: 'rfq',
    solucion: 'sourcing',
    label: 'RFQ',
    icon: '📋',
    description: 'Integración de solicitudes de cotización y requerimientos de sourcing.',
    steps: PASOS_GENERICOS('la integración de RFQ')
  },
  {
    slug: 'orden-compra',
    solucion: 'sourcing',
    label: 'Orden de Compra',
    icon: '🧾',
    description: 'Integración del proceso de adjudicación de un evento hacia la Orden de Compra.',
    steps: PASOS_GENERICOS('la generación de Orden de Compra desde la adjudicación')
  },
  {
    slug: 'contrato',
    solucion: 'sourcing',
    label: 'Contrato',
    icon: '📑',
    description: 'Generación y vínculo de la adjudicación con los acuerdos contractuales.',
    steps: PASOS_GENERICOS('el vínculo con acuerdos contractuales')
  },

  // ── Contracts ──────────────────────────────────────────────────────────
  {
    slug: 'contratos',
    solucion: 'contracts',
    label: 'Contratos',
    icon: '📚',
    description: 'Integración del workspace de contratos y datos maestros con el ERP.',
    steps: PASOS_GENERICOS('la integración del workspace de contratos con el ERP')
  }
];

export const getCanalesDeSolucion = (solucion: string): CanalIntegracionDef[] =>
  CANALES_INTEGRACION.filter((c) => c.solucion === solucion);

export const encontrarCanal = (solucion: string, slug: string): CanalIntegracionDef | undefined =>
  CANALES_INTEGRACION.find((c) => c.solucion === solucion && c.slug === slug);
